import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdCloudUpload } from 'react-icons/md';
import axiosInstance from '../../api/axiosInstance';

// Static service route options — these map to the hardcoded detail pages
const SERVICE_ROUTES = [
  { label: '💻 Web Development  →  /webdeveloper',    value: '/webdeveloper' },
  { label: '📱 App Development  →  /appdevelopment',  value: '/appdevelopment' },
  { label: '🎨 UI / UX Design   →  /uiuxdesign',      value: '/uiuxdesign' },
  { label: '🛍  CMS Development  →  /cmsdevelopment',  value: '/cmsdevelopment' },
  { label: '📊 Digital Marketing →  /digitalmarketing',value: '/digitalmarketing' },
  { label: '🔍 SEO Optimization  →  /seooptimization', value: '/seooptimization' },
  { label: '🖌  Graphic Designing →  /graphicdesigning',value: '/graphicdesigning' },
];

// Icon choices
const ICON_OPTIONS = [
  { label: '💻 Web Development',  value: 'MdCode' },
  { label: '📱 App Development',  value: 'MdSmartphone' },
  { label: '🎨 UI / UX',         value: 'MdPalette' },
  { label: '🔍 SEO',             value: 'MdSearch' },
  { label: '📊 Marketing',       value: 'MdBarChart' },
  { label: '🖌  Design',          value: 'MdBrush' },
];

const ServiceCreate = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('MdCode');
  const [path, setPath] = useState('/webdeveloper');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [loading, setLoading] = useState(false);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit.');
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Image is required — must be selected before submitting
    if (!imageFile) {
      alert('⚠️ Please select a Card Thumbnail Image before creating the service.');
      return;
    }

    setLoading(true);

    const data = new FormData();
    data.append('title', title);
    data.append('description', description);
    data.append('icon', icon);
    data.append('path', path);
    data.append('image', imageFile);

    try {
      const response = await axiosInstance.post('/service', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      if (response.data.success) {
        navigate('/admin/services');
      } else {
        alert(response.data.message || 'Failed to create service');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate('/admin/services')}
        className="flex items-center gap-2 text-gray-600 hover:text-[#008300] mb-4 sm:mb-6"
      >
        <MdArrowBack />
        <span>Back to Services</span>
      </button>

      <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Create Service</h1>
      <p className="text-xs sm:text-sm text-gray-500 mb-6 sm:mb-8">
        Service cards are dynamic (admin-controlled). Detail pages are static frontend pages.
      </p>

      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              placeholder="e.g. Web Development"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows="4"
              placeholder="Short description shown on the service card..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none resize-none text-sm sm:text-base"
            />
          </div>

          {/* Icon */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Card Icon</label>
            <select
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            >
              {ICON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Path / Route (Learn More link) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Learn More Route <span className="text-[#008300] font-semibold">(Static Detail Page)</span>
            </label>
            <p className="text-xs text-gray-400 mb-2">
              This controls where the "Learn More" button takes the user. Detail page content is static — not managed here.
            </p>
            <select
              value={path}
              onChange={(e) => setPath(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#008300] focus:border-transparent outline-none text-sm sm:text-base"
            >
              {SERVICE_ROUTES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Choose File Upload Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Thumbnail Image <span className="text-red-500">*</span>
              <span className="text-gray-400 font-normal ml-2 text-xs sm:text-sm">(Required — Please upload a thumbnail to continue)</span>
            </label>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="w-full sm:w-auto">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                  id="service-image-upload"
                />
                <label
                  htmlFor="service-image-upload"
                  className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#008300] hover:bg-green-50/20 transition-all font-medium text-gray-600 text-sm sm:text-base"
                >
                  <MdCloudUpload className="text-lg sm:text-xl text-gray-400" />
                  <span>Choose File</span>
                </label>
              </div>

              {imageFile && (
                <span className="text-xs text-gray-500 truncate max-w-[150px] sm:max-w-[200px]">
                  {imageFile.name}
                </span>
              )}
            </div>

            {/* Image Preview */}
            {imagePreview && (
              <div className="mt-4 relative w-40 h-28 sm:w-48 sm:h-32 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                <img
                  src={imagePreview}
                  alt="Service preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview('');
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 text-xs hover:bg-red-600 transition"
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#008300] text-white py-3 rounded-lg font-semibold hover:bg-[#006600] transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {loading ? 'Creating...' : 'Create Service'}
          </button>
        </form>
      </div>

      {/* Architecture note */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-xs sm:text-sm text-blue-700">
        <strong>Architecture Note:</strong> Only the card (title, description, icon, Learn More route, thumbnail) is managed here.
        The full detail page content (hero, stats, tech stacks, FAQs, etc.) is hardcoded in the frontend and cannot be changed from the admin panel.
      </div>
    </div>
  );
};

export default ServiceCreate;
